import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { VerifiedPhoneNumber } from "@/lib/types";
import { api } from "@/lib/api";

interface VerifiedPhoneNumbersState {
  phoneNumbers: VerifiedPhoneNumber[];
  defaultPhoneNumberId: string | null;
  isLoading: boolean;
  fetchVerifiedPhoneNumbers: (
    organizationId: string
  ) => Promise<VerifiedPhoneNumber[]>;
  setDefaultPhoneNumber: (
    organizationId: string,
    phoneNumberId: string
  ) => Promise<void>;
  getDefaultPhoneNumber: () => VerifiedPhoneNumber | null;
}

export const useVerifiedPhoneNumbersStore = create<VerifiedPhoneNumbersState>()(
  immer((set, get) => ({
    phoneNumbers: [],
    defaultPhoneNumberId: null,
    isLoading: false,

    fetchVerifiedPhoneNumbers: async (organizationId: string) => {
      set({ isLoading: true });
      try {
        const numbers = await api.getVerifiedPhoneNumbers(organizationId);

        // Find the default phone number if any
        const defaultNumber = numbers.find((number) => number.is_default);

        set((state) => {
          state.phoneNumbers = numbers;
          state.defaultPhoneNumberId = defaultNumber?.id || null;
          state.isLoading = false;
        });

        return numbers;
      } catch (error) {
        console.error("Failed to fetch verified phone numbers:", error);
        set({ isLoading: false });
        return [];
      }
    },

    setDefaultPhoneNumber: async (
      organizationId: string,
      phoneNumberId: string
    ) => {
      try {
        await api.setOrganizationDefaultPhoneNumber(
          organizationId,
          phoneNumberId
        );

        set((state) => {
          state.defaultPhoneNumberId = phoneNumberId;

          // Update is_default flag on all phone numbers
          state.phoneNumbers = state.phoneNumbers.map((number) => ({
            ...number,
            is_default: number.id === phoneNumberId,
          }));
        });
      } catch (error) {
        console.error("Failed to set default phone number:", error);
        throw error;
      }
    },

    getDefaultPhoneNumber: () => {
      const { phoneNumbers, defaultPhoneNumberId } = get();
      return (
        phoneNumbers.find((number) => number.id === defaultPhoneNumberId) ||
        null
      );
    },
  }))
);
